import { queryOptions, useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  CalendarIcon,
  GitPullRequestIcon,
  MessageSquareIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExternalLinkIcon,
} from 'lucide-react';
import { useState, Suspense } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/clients';
import { cn } from '@/lib/utils';

type Review = {
  gid: string;
  id: number;
  state: string;
  body: string;
  author: {
    login: string;
    id: number;
    avatarUrl: string;
  } | null;
  createdAt: string;
  agentVersion?: string;
  commentCount: number;
  upvotes: number;
  downvotes: number;
};

type PullWithReviews = {
  nodeId: string;
  id: number;
  owner: string;
  repo: string;
  number: number;
  title: string;
  state: string;
  author: {
    login: string;
    id: number;
    avatarUrl: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  labels: { name: string; color: string }[] | null;
  additions: number;
  deletions: number;
  reviews: Review[];
};

type ReviewsResponse = {
  pulls: PullWithReviews[];
  greptileReviews?: Review[];
  coderabbitReviews?: Review[];
  ellipsisReviews?: Review[];
  cursorReviews?: Review[];
};

// Version parsing utilities
type VersionInfo = {
  major: number;
  minor: number;
  full: string;
};

const parseVersion = (version: string | undefined): VersionInfo | null => {
  if (!version) return null;

  // Parse versions like "2.1", "2.1.0", "v2.1", etc.
  const match = version.match(/v?(\d+)\.(\d+)(?:\.\d+)?/);
  if (!match) return null;

  return {
    major: parseInt(match[1], 10),
    minor: parseInt(match[2], 10),
    full: `${match[1]}.${match[2]}`,
  };
};

// Calculate error rate for a review
const calculateErrorRate = (review: Review): number => {
  const commentCount = Number(review.commentCount) || 0;
  const downvotes = Number(review.downvotes) || 0;

  if (commentCount === 0) return 0;
  return (downvotes / commentCount) * 100;
};

// Aggregation types
type VersionStats = {
  version: string;
  reviewCount: number;
  totalComments: number;
  avgErrorRate: number;
  minErrorRate: number;
  maxErrorRate: number;
  errorRates: number[]; // For validation
};

// Aggregate reviews by version
const aggregateReviewsByVersion = (
  pulls: PullWithReviews[] | undefined
): {
  byMinorVersion: VersionStats[];
  byMajorVersion: VersionStats[];
} => {
  if (!pulls) return { byMinorVersion: [], byMajorVersion: [] };

  // Collect all reviews with versions
  const reviewsWithVersions: Array<{ review: Review; version: VersionInfo }> = [];

  pulls.forEach((pull) => {
    pull.reviews.forEach((review) => {
      const version = parseVersion(review.agentVersion);
      if (version) {
        reviewsWithVersions.push({ review, version });
      }
    });
  });

  // Group by minor version
  const minorVersionMap = new Map<string, Review[]>();
  reviewsWithVersions.forEach(({ review, version }) => {
    const key = version.full;
    if (!minorVersionMap.has(key)) {
      minorVersionMap.set(key, []);
    }
    minorVersionMap.get(key)!.push(review);
  });

  // Group by major version
  const majorVersionMap = new Map<number, Review[]>();
  reviewsWithVersions.forEach(({ review, version }) => {
    const key = version.major;
    if (!majorVersionMap.has(key)) {
      majorVersionMap.set(key, []);
    }
    majorVersionMap.get(key)!.push(review);
  });

  // Calculate stats for each group
  const calculateStats = (version: string, reviews: Review[]): VersionStats => {
    // Convert all string numbers to actual numbers
    const reviewsWithNumbers = reviews.map((r) => ({
      ...r,
      commentCount: Number(r.commentCount) || 0,
      upvotes: Number(r.upvotes) || 0,
      downvotes: Number(r.downvotes) || 0,
    }));

    const errorRates = reviewsWithNumbers
      .filter((r) => r.commentCount > 0) // Only include reviews with comments
      .map((r) => calculateErrorRate(r));

    const totalComments = reviewsWithNumbers.reduce((sum, r) => sum + r.commentCount, 0);

    if (errorRates.length === 0) {
      return {
        version,
        reviewCount: reviews.length,
        totalComments,
        avgErrorRate: 0,
        minErrorRate: 0,
        maxErrorRate: 0,
        errorRates: [],
      };
    }

    return {
      version,
      reviewCount: reviews.length,
      totalComments,
      avgErrorRate: errorRates.reduce((sum, rate) => sum + rate, 0) / errorRates.length,
      minErrorRate: Math.min(...errorRates),
      maxErrorRate: Math.max(...errorRates),
      errorRates, // For debugging/validation
    };
  };

  // Sort and calculate stats
  const byMinorVersion = Array.from(minorVersionMap.entries())
    .map(([version, reviews]) => calculateStats(version, reviews))
    .sort((a, b) => {
      const aVer = parseVersion(a.version)!;
      const bVer = parseVersion(b.version)!;
      return aVer.major !== bVer.major ? aVer.major - bVer.major : aVer.minor - bVer.minor;
    });

  const byMajorVersion = Array.from(majorVersionMap.entries())
    .map(([version, reviews]) => {
      // Convert all string numbers to actual numbers for major version calculation too
      const reviewsWithNumbers = reviews.map((r) => ({
        ...r,
        commentCount: Number(r.commentCount) || 0,
        upvotes: Number(r.upvotes) || 0,
        downvotes: Number(r.downvotes) || 0,
      }));

      const errorRates = reviewsWithNumbers.filter((r) => r.commentCount > 0).map((r) => calculateErrorRate(r));

      const totalComments = reviewsWithNumbers.reduce((sum, r) => sum + r.commentCount, 0);

      if (errorRates.length === 0) {
        return {
          version: `${version}.x`,
          reviewCount: reviews.length,
          totalComments,
          avgErrorRate: 0,
          minErrorRate: 0,
          maxErrorRate: 0,
          errorRates: [],
        };
      }

      return {
        version: `${version}.x`,
        reviewCount: reviews.length,
        totalComments,
        avgErrorRate: errorRates.reduce((sum, rate) => sum + rate, 0) / errorRates.length,
        minErrorRate: Math.min(...errorRates),
        maxErrorRate: Math.max(...errorRates),
        errorRates,
      };
    })
    .sort((a, b) => {
      const aMajor = parseInt(a.version);
      const bMajor = parseInt(b.version);
      return aMajor - bMajor;
    });

  return { byMinorVersion, byMajorVersion };
};

const reviewsQuery = (filters: { owner?: string; repo?: string; startDate?: string; endDate?: string }) =>
  queryOptions({
    queryKey: ['admin', 'reviews', filters],
    queryFn: async () => {
      const response = await api.admin.reviews.$get({
        query: filters,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch reviews');
      }

      const data = await response.json();
      return {
        pulls: data.pulls as PullWithReviews[],
        greptileReviews: data.greptileReviews as Review[] | undefined,
        coderabbitReviews: data.coderabbitReviews as Review[] | undefined,
        ellipsisReviews: data.ellipsisReviews as Review[] | undefined,
        cursorReviews: data.cursorReviews as Review[] | undefined,
      };
    },
  });

export const Route = createFileRoute('/_authed/_layout/admin/')({
  component: RouteComponent,
});

function LoadingState() {
  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto p-8">
        <h1 className="text-3xl font-bold mb-8">Reviews</h1>
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">Loading reviews...</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function RouteComponent() {
  return (
    <Suspense fallback={<LoadingState />}>
      <ReviewsDashboard />
    </Suspense>
  );
}

// Review Quality Analytics Component
function ReviewQualityAnalytics({
  pulls,
  greptileReviews,
  coderabbitReviews,
  ellipsisReviews,
  cursorReviews,
}: {
  pulls: PullWithReviews[] | undefined;
  greptileReviews?: Review[];
  coderabbitReviews?: Review[];
  ellipsisReviews?: Review[];
  cursorReviews?: Review[];
}) {
  const { byMinorVersion, byMajorVersion } = aggregateReviewsByVersion(pulls);

  const computeBaselineStats = (reviews?: Review[]) => {
    if (!reviews || reviews.length === 0) return null;

    const reviewsWithNumbers = reviews.map((r) => ({
      ...r,
      commentCount: Number(r.commentCount) || 0,
      upvotes: Number(r.upvotes) || 0,
      downvotes: Number(r.downvotes) || 0,
    }));

    const errorRates = reviewsWithNumbers.filter((r) => r.commentCount > 0).map((r) => calculateErrorRate(r));

    const totalComments = reviewsWithNumbers.reduce((sum, r) => sum + r.commentCount, 0);

    if (errorRates.length === 0) {
      return {
        reviewCount: reviews.length,
        totalComments,
        avgErrorRate: 0,
        minErrorRate: 0,
        maxErrorRate: 0,
      } as const;
    }

    return {
      reviewCount: reviews.length,
      totalComments,
      avgErrorRate: errorRates.reduce((sum, rate) => sum + rate, 0) / errorRates.length,
      minErrorRate: Math.min(...errorRates),
      maxErrorRate: Math.max(...errorRates),
    } as const;
  };

  const greptileStats = computeBaselineStats(greptileReviews);
  const coderabbitStats = computeBaselineStats(coderabbitReviews);
  const ellipsisStats = computeBaselineStats(ellipsisReviews);
  const cursorStats = computeBaselineStats(cursorReviews);

  // Format percentage with 1 decimal place
  const formatPercent = (value: number) => `${value.toFixed(1)}%`;

  // Count total reviews with versions
  const totalVersionedReviews =
    pulls?.reduce((sum, pull) => sum + pull.reviews.filter((r) => r.agentVersion).length, 0) ?? 0;

  if (totalVersionedReviews === 0 && !greptileStats && !coderabbitStats && !ellipsisStats && !cursorStats) {
    return null;
  }

  return (
    <div className="space-y-6 mb-8">
      {/* Summary Stats */}
      <Card>
        <CardHeader>
          <CardTitle>Review Quality Analytics</CardTitle>
          <CardDescription>
            Error rates across different reviewer versions. Error rate = (downvoted comments / total comments)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground mb-4">
            Analyzing {totalVersionedReviews} Mesa reviews with version information
            {(() => {
              const baselines: string[] = [];
              if (greptileStats) baselines.push(`${greptileStats.reviewCount} Greptile reviews`);
              if (coderabbitStats) baselines.push(`${coderabbitStats.reviewCount} Coderabbit reviews`);
              if (ellipsisStats) baselines.push(`${ellipsisStats.reviewCount} Ellipsis reviews`);
              if (cursorStats) baselines.push(`${cursorStats.reviewCount} Cursor reviews`);
              return baselines.length > 0 ? ` and ${baselines.join(' & ')} as baseline` : '';
            })()}
          </div>

          {/* Major Version Stats */}
          <div className="mb-6">
            <h4 className="font-medium mb-3">By Major Version</h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead className="text-right">Reviews</TableHead>
                  <TableHead className="text-right">Total Comments</TableHead>
                  <TableHead className="text-right">Avg Error Rate</TableHead>
                  <TableHead className="text-right">Min Error Rate</TableHead>
                  <TableHead className="text-right">Max Error Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Baseline Rows */}
                {[
                  { label: 'Greptile', stats: greptileStats },
                  { label: 'Coderabbit', stats: coderabbitStats },
                  { label: 'Ellipsis', stats: ellipsisStats },
                  { label: 'Cursor', stats: cursorStats },
                ]
                  .filter((b) => b.stats)
                  .map(({ label, stats }) => (
                    <TableRow key={label} className="bg-muted/50">
                      <TableCell className="font-medium">{label}</TableCell>
                      <TableCell className="text-right">{stats!.reviewCount}</TableCell>
                      <TableCell className="text-right">{stats!.totalComments}</TableCell>
                      <TableCell className="text-right">
                        <span
                          className={cn(
                            stats!.avgErrorRate > 10 && 'text-red-600 dark:text-red-400',
                            stats!.avgErrorRate > 5 &&
                              stats!.avgErrorRate <= 10 &&
                              'text-yellow-600 dark:text-yellow-400',
                            stats!.avgErrorRate <= 5 && 'text-green-600 dark:text-green-400'
                          )}
                        >
                          {formatPercent(stats!.avgErrorRate)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{formatPercent(stats!.minErrorRate)}</TableCell>
                      <TableCell className="text-right">{formatPercent(stats!.maxErrorRate)}</TableCell>
                    </TableRow>
                  ))}
                {/* Separator row if we have both baseline and mesa data */}
                {(greptileStats || coderabbitStats || ellipsisStats || cursorStats) && byMajorVersion.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="h-2 p-0 bg-border/50"></TableCell>
                  </TableRow>
                )}
                {byMajorVersion.length === 0 && !greptileStats && !coderabbitStats && !ellipsisStats && !cursorStats ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground">
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  byMajorVersion.map((stats) => (
                    <TableRow key={stats.version}>
                      <TableCell className="font-medium">{stats.version}</TableCell>
                      <TableCell className="text-right">{stats.reviewCount}</TableCell>
                      <TableCell className="text-right">{stats.totalComments}</TableCell>
                      <TableCell className="text-right">
                        <span
                          className={cn(
                            stats.avgErrorRate > 10 && 'text-red-600 dark:text-red-400',
                            stats.avgErrorRate > 5 &&
                              stats.avgErrorRate <= 10 &&
                              'text-yellow-600 dark:text-yellow-400',
                            stats.avgErrorRate <= 5 && 'text-green-600 dark:text-green-400'
                          )}
                        >
                          {formatPercent(stats.avgErrorRate)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{formatPercent(stats.minErrorRate)}</TableCell>
                      <TableCell className="text-right">{formatPercent(stats.maxErrorRate)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Minor Version Stats - Collapsible */}
          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button variant="outline" size="sm">
                View Detailed Minor Version Stats
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-4">
              <h4 className="font-medium mb-3">By Minor Version</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Version</TableHead>
                    <TableHead className="text-right">Reviews</TableHead>
                    <TableHead className="text-right">Total Comments</TableHead>
                    <TableHead className="text-right">Avg Error Rate</TableHead>
                    <TableHead className="text-right">Min Error Rate</TableHead>
                    <TableHead className="text-right">Max Error Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {byMinorVersion.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    byMinorVersion.map((stats) => (
                      <TableRow key={stats.version}>
                        <TableCell className="font-medium">{stats.version}</TableCell>
                        <TableCell className="text-right">{stats.reviewCount}</TableCell>
                        <TableCell className="text-right">{stats.totalComments}</TableCell>
                        <TableCell className="text-right">
                          <span
                            className={cn(
                              stats.avgErrorRate > 10 && 'text-red-600 dark:text-red-400',
                              stats.avgErrorRate > 5 &&
                                stats.avgErrorRate <= 10 &&
                                'text-yellow-600 dark:text-yellow-400',
                              stats.avgErrorRate <= 5 && 'text-green-600 dark:text-green-400'
                            )}
                          >
                            {formatPercent(stats.avgErrorRate)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{formatPercent(stats.minErrorRate)}</TableCell>
                        <TableCell className="text-right">{formatPercent(stats.maxErrorRate)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CollapsibleContent>
          </Collapsible>

          {/* Validation Helper - Only in development */}
          {import.meta.env.DEV && (
            <details className="mt-4 text-xs text-muted-foreground">
              <summary className="cursor-pointer">Debug: Sample calculations for validation</summary>
              <pre className="mt-2 p-2 bg-muted rounded overflow-auto select-text">
                {JSON.stringify(
                  {
                    sampleReviews: pulls?.slice(0, 3).flatMap((p) =>
                      p.reviews.slice(0, 2).map((r) => ({
                        version: r.agentVersion,
                        comments: r.commentCount,
                        commentsType: typeof r.commentCount,
                        commentsAsNumber: Number(r.commentCount) || 0,
                        downvotes: r.downvotes,
                        downvotesType: typeof r.downvotes,
                        downvotesAsNumber: Number(r.downvotes) || 0,
                        errorRate: calculateErrorRate(r).toFixed(2) + '%',
                      }))
                    ),
                    errorRateDistribution: (() => {
                      const allErrorRates =
                        pulls?.flatMap((p) =>
                          p.reviews
                            .filter((r) => r.agentVersion && Number(r.commentCount) > 0)
                            .map((r) => calculateErrorRate(r))
                        ) || [];

                      // Also get the actual reviews for better debugging
                      const reviewsWithComments =
                        pulls?.flatMap((p) => p.reviews.filter((r) => r.agentVersion && Number(r.commentCount) > 0)) ||
                        [];

                      return {
                        totalReviewsWithComments: allErrorRates.length,
                        totalReviewsWithVersions:
                          pulls?.flatMap((p) => p.reviews.filter((r) => r.agentVersion)).length || 0,
                        reviewsFilteredOut:
                          (pulls?.flatMap((p) => p.reviews.filter((r) => r.agentVersion)).length || 0) -
                          allErrorRates.length,
                        zeroErrorRates: allErrorRates.filter((rate) => rate === 0).length,
                        nonZeroErrorRates: allErrorRates.filter((rate) => rate > 0).length,
                        sampleNonZeroRates: allErrorRates.filter((rate) => rate > 0).slice(0, 10),
                        allRatesSorted: allErrorRates.sort((a, b) => a - b).slice(0, 20),
                        average:
                          allErrorRates.length > 0
                            ? allErrorRates.reduce((sum, rate) => sum + rate, 0) / allErrorRates.length
                            : 0,
                        sampleReviewsIncluded: reviewsWithComments.slice(0, 5).map((r) => ({
                          version: r.agentVersion,
                          comments: Number(r.commentCount),
                          downvotes: Number(r.downvotes),
                          errorRate: calculateErrorRate(r),
                        })),
                      };
                    })(),
                    versionParsing: ['2.1', 'v2.1.0', '3.0'].map((v) => ({
                      input: v,
                      parsed: parseVersion(v),
                    })),
                    aggregationTest:
                      byMajorVersion.length > 0
                        ? {
                            firstMajorVersion: byMajorVersion[0],
                            sampleReviewsForFirstVersion: pulls
                              ?.flatMap((p) => p.reviews)
                              .filter((r) => {
                                const version = parseVersion(r.agentVersion);
                                const majorVersionNumber = parseInt(byMajorVersion[0].version.replace('.x', ''));
                                return version?.major === majorVersionNumber;
                              })
                              .slice(0, 3)
                              .map((r) => ({
                                commentCount: Number(r.commentCount) || 0,
                                downvotes: Number(r.downvotes) || 0,
                                errorRate: calculateErrorRate(r),
                              })),
                          }
                        : null,
                  },
                  null,
                  2
                )}
              </pre>
            </details>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ReviewsDashboard() {
  const [appliedFilters, setAppliedFilters] = useState({
    owner: '',
    repo: '',
    startDate: '',
    endDate: '',
    excludeDevReviews: false,
  });

  const [formFilters, setFormFilters] = useState({
    owner: '',
    repo: '',
    startDate: '',
    endDate: '',
    excludeDevReviews: false,
  });

  const [expandedPulls, setExpandedPulls] = useState<Set<string>>(new Set());

  const { data, isLoading: isLoadingReviews } = useQuery(reviewsQuery(appliedFilters));

  // Destructure pulls and baseline reviews from the data
  const rawPulls = data?.pulls;
  const greptileReviews = data?.greptileReviews;
  const coderabbitReviews = data?.coderabbitReviews;
  const ellipsisReviews = data?.ellipsisReviews;
  const cursorReviews = data?.cursorReviews;

  // Filter out dev reviews on the frontend if the toggle is enabled
  const pulls = rawPulls
    ?.map((pull) => {
      if (!appliedFilters.excludeDevReviews) {
        return pull;
      }

      const filteredReviews = pull.reviews.filter((review) => {
        const authorLogin = review.author?.login;
        return authorLogin !== 'mesa-dev-development' && authorLogin !== 'mesa-dev-development[bot]';
      });

      return {
        ...pull,
        reviews: filteredReviews,
      };
    })
    .filter((pull) => pull.reviews.length > 0 || !appliedFilters.excludeDevReviews);

  const handleFilterChange = (key: keyof typeof formFilters, value: string | boolean) => {
    setFormFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(formFilters);
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      owner: '',
      repo: '',
      startDate: '',
      endDate: '',
      excludeDevReviews: false,
    };
    setFormFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  const togglePull = (pullId: string) => {
    setExpandedPulls((prev) => {
      const next = new Set(prev);
      if (next.has(pullId)) {
        next.delete(pullId);
      } else {
        next.add(pullId);
      }
      return next;
    });
  };

  const hasActiveFilters = Object.values(appliedFilters).some((value) => value !== '' && value !== false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPullStateColor = (state: string) => {
    switch (state) {
      case 'OPEN':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'MERGED':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'CLOSED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getReviewStateColor = (state: string) => {
    switch (state) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'CHANGES_REQUESTED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'COMMENTED':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'DISMISSED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const totalReviews = pulls?.reduce((sum, pull) => sum + pull.reviews.length, 0) ?? 0;

  return (
    <div className="h-full overflow-y-auto">
      <div className="container mx-auto py-8 px-4">
        <h1 className="text-3xl font-bold mb-8">Reviews</h1>

        {/* Filters */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Filters</CardTitle>
            <CardDescription>Filter reviews by owner, repository, or date range</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="owner">Owner</Label>
                  <Input
                    id="owner"
                    placeholder="e.g., octocat"
                    value={formFilters.owner}
                    onChange={(e) => handleFilterChange('owner', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="repo">Repository</Label>
                  <Input
                    id="repo"
                    placeholder="e.g., hello-world"
                    value={formFilters.repo}
                    onChange={(e) => handleFilterChange('repo', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={formFilters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={formFilters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="excludeDevReviews"
                  checked={formFilters.excludeDevReviews}
                  onCheckedChange={(checked) => handleFilterChange('excludeDevReviews', checked)}
                />
                <Label htmlFor="excludeDevReviews">Exclude dev reviews</Label>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleApplyFilters} className="px-6">
                  Apply Filters
                </Button>
                {hasActiveFilters && (
                  <Button onClick={handleResetFilters} variant="outline">
                    Reset
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Review Quality Analytics */}
        {!isLoadingReviews && pulls && (
          <ReviewQualityAnalytics
            pulls={pulls}
            greptileReviews={greptileReviews}
            coderabbitReviews={coderabbitReviews}
            ellipsisReviews={ellipsisReviews}
            cursorReviews={cursorReviews}
          />
        )}

        {/* Pull Requests with Reviews */}
        {isLoadingReviews ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Loading reviews...</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pulls?.length === 0 ? (
              <Card>
                <CardContent className="text-center py-8">
                  <p className="text-muted-foreground">No pull requests with reviews found matching the filters.</p>
                </CardContent>
              </Card>
            ) : (
              pulls?.map((pull) => (
                <Card key={pull.nodeId} className="overflow-hidden">
                  <Collapsible
                    open={expandedPulls.has(pull.nodeId)}
                    onOpenChange={() => togglePull(pull.nodeId)}
                    className="p-0"
                  >
                    <CollapsibleTrigger className="w-full p-0">
                      <div className="px-6 pb-4 hover:bg-accent/50 transition-colors cursor-pointer">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="flex items-center gap-1">
                                {expandedPulls.has(pull.nodeId) ? (
                                  <ChevronDownIcon className="h-4 w-4" />
                                ) : (
                                  <ChevronRightIcon className="h-4 w-4" />
                                )}
                                <GitPullRequestIcon className="h-5 w-5" />
                              </div>
                              <Link
                                to="/$owner/$repo/pull/$number"
                                params={{
                                  owner: pull.owner,
                                  repo: pull.repo,
                                  number: pull.number,
                                }}
                                className="text-lg font-semibold hover:underline text-primary"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {pull.title}
                              </Link>
                              <a
                                href={`https://github.com/${pull.owner}/${pull.repo}/pull/${pull.number}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="ml-2 p-1 rounded hover:bg-accent/50 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                                title="View on GitHub"
                              >
                                <ExternalLinkIcon className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                              </a>
                              <Badge className={cn('ml-2', getPullStateColor(pull.state))}>{pull.state}</Badge>
                            </div>
                            <h3 className="text-base font-medium mb-2 w-fit text-muted-foreground">
                              {pull.owner}/{pull.repo} #{pull.number}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <MessageSquareIcon className="h-4 w-4" />
                                <span>{pull.reviews.length} reviews</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <span>+{pull.additions}</span>
                                <span>-{pull.deletions}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <CalendarIcon className="h-4 w-4" />
                                <span>{formatDate(pull.createdAt)}</span>
                              </div>
                            </div>
                          </div>
                          {pull.author && (
                            <div className="flex items-center gap-2 ml-4">
                              <img
                                src={pull.author.avatarUrl}
                                alt={pull.author.login}
                                className="h-8 w-8 rounded-full"
                              />
                              <span className="text-sm text-muted-foreground">{pull.author.login}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="border-t bg-muted/20">
                        {pull.reviews.length === 0 ? (
                          <div className="p-4 text-center text-muted-foreground">
                            No reviews found for this pull request.
                          </div>
                        ) : (
                          <div className="divide-y">
                            {pull.reviews.map((review) => (
                              <div key={review.gid} className="p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    {review.author && (
                                      <>
                                        <img
                                          src={review.author.avatarUrl}
                                          alt={review.author.login}
                                          className="h-6 w-6 rounded-full"
                                        />
                                        <span className="text-sm font-medium">{review.author.login}</span>
                                      </>
                                    )}
                                    <Badge className={cn('text-xs', getReviewStateColor(review.state))}>
                                      {review.state.replace('_', ' ')}
                                    </Badge>
                                    {review.agentVersion && (
                                      <span className="text-xs text-muted-foreground">v{review.agentVersion}</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                      <MessageSquareIcon className="h-3 w-3" />
                                      <span>{review.commentCount}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <span>👍</span>
                                      <span>{review.upvotes}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <span>👎</span>
                                      <span>{review.downvotes}</span>
                                    </div>
                                    <span>{formatDate(review.createdAt)}</span>
                                  </div>
                                </div>
                                {review.body && (
                                  <div className="mt-2 text-sm text-muted-foreground line-clamp-3">{review.body}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </Card>
              ))
            )}

            {/* Summary */}
            <div className="mt-8 mb-4 text-center text-sm text-muted-foreground">
              Showing {pulls?.length} pull request{pulls?.length !== 1 ? 's' : ''} with {totalReviews} review
              {totalReviews !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
